import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Search } from '@/components/ui';
import type { MaintenanceWorkload, WorkloadStatus } from '../types/maintenance-workload.types';
import { useMaintenanceWorkloads } from '../hooks/useMaintenanceWorkload';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useZones } from '@/modules/maintenance/zones/hooks/useMaintenanceZones';
import { toast } from 'sonner';

interface WorkloadFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  workload?: MaintenanceWorkload | null;
  companyId: string;
}

export function WorkloadFormModal({ isOpen, onClose, workload, companyId }: WorkloadFormModalProps) {
  const isEdit = !!workload;
  const { createWorkload, updateWorkload, isCreating, isUpdating } = useMaintenanceWorkloads(companyId);
  const { projects = [] } = useProjects(companyId);
  const { zones = [] } = useZones(companyId);

  const [formData, setFormData] = useState<Partial<MaintenanceWorkload>>({
    title: '',
    description: '',
    project_id: '',
    zone_id: '',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
    status: 'draft',
  });

  useEffect(() => {
    if (isOpen) {
      if (workload) {
        setFormData(workload);
      } else {
        setFormData({
          title: '',
          description: '',
          project_id: '',
          zone_id: '',
          period_start: new Date().toISOString().split('T')[0],
          period_end: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0],
          status: 'draft',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, workload, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEdit && workload) {
        await updateWorkload({ id: workload.id, updates: formData });
      } else {
        await createWorkload({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật workload thành công' : 'Khởi tạo workload mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Cập nhật Workload' : 'Khởi tạo Workload mới'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6 py-2">
        <Input
          label="Tiêu đề Workload *"
          required
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="VD: Workload bảo dưỡng Zone A - Tháng 05/2026"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dự án *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <option value="">-- Chọn dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Khu vực (Zone) *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.zone_id || ''}
              onChange={(e) => setFormData({ ...formData, zone_id: e.target.value })}
            >
              <option value="">-- Chọn khu vực --</option>
              {zones.filter(z => z.project_id === formData.project_id).map(z => (
                <option key={z.id} value={z.id}>{z.zone_name} ({z.zone_code})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Ngày bắt đầu"
            type="date"
            value={formData.period_start ? new Date(formData.period_start).toISOString().split('T')[0] : ''}
            onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
          />
          <Input
            label="Ngày kết thúc"
            type="date"
            value={formData.period_end ? new Date(formData.period_end).toISOString().split('T')[0] : ''}
            onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Mô tả mục tiêu</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-3 min-h-[100px]"
            placeholder="Mô tả phạm vi hoặc các lưu ý đặc biệt cho đợt tính toán khối lượng này..."
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-50">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>Hủy</Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Khởi tạo Workload'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
